import { inject, Injectable } from '@angular/core';
import {
  Contact,
  ContactConnection,
  ContactDropdown,
  ContactDropdownConnection,
} from 'src/app/model/contacts/contact';
import { SelectItem } from '@openng/optimus-ui/api';
import { ContactUpdateDto } from 'src/app/model/contacts/contact-update-dto';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import { getCorrectedDate, isNullOrUndefined } from '../../utils/utils';
import {
  ContactMessageKeys,
  ValidationConfig,
} from 'src/app/core/type-helpers/validation-msg';
import { ContactExistResult } from 'src/app/model/contacts/contact-exist-result';
import { JobRole, JobRoleConnection } from 'src/app/model/admin/job-role';
import { MocEventBuilderService } from './moc-event-builder.service';

@Injectable({
  providedIn: 'root',
})
export class ContactBuilderService {
  protected _mocBuilder = inject(MocEventBuilderService);
  availableContactMethods: SelectItem[];

  constructor() {
    this.availableContactMethods = [
      { label: 'Select Option', value: null },
      { label: 'Business Phone', value: 'OFFICE' },
      { label: 'Business Mobile', value: 'CELLULAR' },
      { label: 'Home', value: 'HOME' },
      { label: 'Personal Mobile', value: 'PERSONAL CELL' },
      { label: 'Speed Dial', value: 'SPEED DIAL' },
      { label: 'Other', value: 'OTHER' },
    ];
  }

  buildContactValidationMessages(): ValidationConfig<ContactMessageKeys> {
    return {
      messages: {
        businessPhone: {
          pattern: 'CONTACT.VALIDATION.BUSINESS_PHONE.BUSINESS_PHONE_PATTERN',
        },
        businessMobile: {
          pattern: 'CONTACT.VALIDATION.BUSINESS_MOBILE.BUSINESS_MOBILE_PATTERN',
        },
        home: {
          pattern: 'CONTACT.VALIDATION.HOME.HOME_PATTERN',
        },
        other: {
          pattern: 'CONTACT.VALIDATION.OTHER.OTHER_PHONE_PATTERN',
        },
        personalMobile: {
          pattern: 'CONTACT.VALIDATION.PERSONAL_MOBILE.PERSONAL_MOBILE_PATTERN',
        },
        supervisor: {
          required: 'CONTACT.VALIDATION.SUPERVISOR.SUPERVISOR_REQUIRED',
        },
        afterHoursContactMethod: {
          value: 'CONTACT.VALIDATION.SUPERVISOR.SUPERVISOR_REQUIRED',
        },
        nickName: {
          pattern: 'CONTACT.VALIDATION.NICKNAME.NICKNAME_ALPHA_ONLY',
        },
        city: {
          required: 'CONTACT.VALIDATION.CITY.CITY_REQUIRED',
        },
      },
      params: {},
    };
  }

  buildContactQuery(filters: any) {
    const query = {
      firstName: null,
      lastName: null,
      fullName: null,
      company: null,
      businessPhone: null,
      businessMobile: null,
      adAccountName: null,
      activeStatus: null,
      supervisor: null,
      jobRole: null,
      isUnavailable: null,
      businessUnits: null,
    };

    if (filters.firstName) {
      query.firstName = filters.firstName.value;
    }
    if (filters.lastName) {
      query.lastName = filters.lastName.value;
    }
    if (filters.fullName) {
      query.fullName = filters.fullName.value;
    }
    if (filters.company) {
      query.company = filters.company.value;
    }
    if (filters.businessPhone) {
      query.businessPhone = filters.businessPhone.value;
    }
    if (filters.businessMobile) {
      query.businessMobile = filters.businessMobile.value;
    }
    if (filters.adAccountName) {
      query.adAccountName = filters.adAccountName.value;
    }
    if (filters.activeStatus) {
      query.activeStatus = filters.activeStatus.value;
    }
    if (filters.supervisor) {
      query.supervisor = filters.supervisor.value;
    }
    if (filters.jobRole) {
      query.jobRole = filters.jobRole.value;
    }
    if (filters.isUnavailable) {
      query.isUnavailable = filters.isUnavailable.value;
    }
    if (filters.businessUnits) {
      query.businessUnits = filters.businessUnits;
    }

    return query;
  }

  buildContact(i: any): Contact | null {
    if (isNullOrUndefined(i)) {
      return null;
    }

    // Availability times come in as UTC
    i.Availabilities?.forEach((avail: any) => {
      if (
        avail.start &&
        typeof avail.start === 'string' &&
        !avail.start.includes('.000+00:00')
      ) {
        avail.start += '.000+00:00';
      }
      avail.start = new Date(avail.start);
      if (
        avail.end &&
        typeof avail.end === 'string' &&
        !avail.end.includes('.000+00:00')
      ) {
        avail.end += '.000+00:00';
      }
      avail.end = new Date(avail.end);
    });
    return {
      firstName: i.firstName,
      lastName: i.lastName,
      fullName: i.firstName + ' ' + i.lastName,
      nickName: i.nickName,
      activeStatus:
        i.inactiveAdAccount && i.inactiveAdAccount === true
          ? 'INACTIVE'
          : 'ACTIVE',
      inactiveAdAccount: i.inactiveAdAccount ? i.inactiveAdAccount : false,
      afterHoursContactMethod: i.afterHoursPhoneNumber
        ? this.availableContactMethods.filter(
            (s) => s.value === i.afterHoursPhoneNumber
          )[0]
        : null,
      speedDial: i.speedDial,
      companyVersion: i.Company ? i.Company.version : null,
      company: i.Company ? i.Company.name : null,
      companyAbbreviation: i.Company ? i.Company.abbreviation : null,
      companyItem: i.Company
        ? { label: i.Company.name, value: i.Company.id }
        : null,
      businessPhone: i.businessPhone ? i.businessPhone : null,
      businessMobile: i.businessMobile ? i.businessMobile : null,
      home: i.home ? i.home : null,
      other: i.other ? i.other : null,
      note: i.note ? i.note : null,
      personalMobile: i.personalMobile ? i.personalMobile : null,
      adAccountName: i.adAccountName ? i.adAccountName : null,
      city: i.city,
      reportsToVersion: i.ReportsTo ? i.ReportsTo.version : null,
      reportsToId: i.ReportsTo ? i.ReportsTo.id : null,
      supervisor: i.ReportsTo
        ? i.ReportsTo.firstName + ' ' + i.ReportsTo.lastName
        : null,
      supervisorBusinessMobile: i.ReportsTo ? i.ReportsTo.businessMobile : null,
      managerId:
        i.ReportsTo && i.ReportsTo.ReportsTo ? i.ReportsTo.ReportsTo.id : null,
      managerLevel:
        i.ReportsTo && i.ReportsTo.ReportsTo
          ? i.ReportsTo.ReportsTo.firstName +
            ' ' +
            i.ReportsTo.ReportsTo.lastName
          : null,
      managerBusinessMobile:
        i.ReportsTo && i.ReportsTo.ReportsTo
          ? i.ReportsTo.ReportsTo.businessMobile
          : null,
      directorId:
        i.ReportsTo && i.ReportsTo.ReportsTo && i.ReportsTo.ReportsTo.ReportsTo
          ? i.ReportsTo.ReportsTo.ReportsTo.id
          : null,
      directorLevel:
        i.ReportsTo && i.ReportsTo.ReportsTo && i.ReportsTo.ReportsTo.ReportsTo
          ? i.ReportsTo.ReportsTo.ReportsTo.firstName +
            ' ' +
            i.ReportsTo.ReportsTo.ReportsTo.lastName
          : null,
      whenUnavailableId: i.WhenUnavailable ? i.WhenUnavailable.id : null,
      whenUnavailableLevel: i.WhenUnavailable
        ? i.WhenUnavailable.firstName + ' ' + i.WhenUnavailable.lastName
        : null,
      jobRoleVersion: i.JobRole ? i.JobRole.version : null,
      jobRoleItem: i.JobRole
        ? { label: i.JobRole.name, value: i.JobRole.id }
        : null,
      jobRole: i.JobRole ? i.JobRole.name : '',
      raw: i,
      id: i.id,
      version: i.version,
      availabilities: i.Availabilities
        ? i.Availabilities.map((item: any) => ({
            id: item.id,
            ContactId: item.ContactId,
            start: item.start,
            end: item.end,
            dirtyStatus: DirtyStatus.UNMODIFIED,
            version: item.version,
          }))
        : [],
      isUnavailable:
        i.Availabilities &&
        i.Availabilities.some(
          (a: any) =>
            new Date(a.start) <= new Date() && new Date(a.end) >= new Date()
        )
          ? 'TRUE'
          : 'FALSE',
      dopAcknowledgements:
        i.DOPAcknowledgements && i.DOPAcknowledgements.length > 0
          ? i.DOPAcknowledgements.map((item: any) => ({
              id: item.id,
              dopGroupId: item.dopGroupId,
              contactId: item.contactId,
              shiftDate: item.shiftDate,
              createdAt: item.createdAt,
              dopState: item.dopState,
            }))
          : [],
    };
  }

  buildNewContact(): Contact {
    return {
      firstName: null,
      lastName: null,
      fullName: null,
      nickName: null,
      activeStatus: null,
      inactiveAdAccount: false,
      afterHoursContactMethod: null,
      speedDial: null,
      companyVersion: null,
      company: null,
      companyAbbreviation: null,
      companyItem: null,
      businessPhone: null,
      businessMobile: null,
      adAccountName: null,
      city: null,
      home: null,
      other: null,
      note: null,
      personalMobile: null,
      reportsToVersion: null,
      reportsToId: null,
      supervisor: null,
      supervisorBusinessMobile: null,
      managerId: null,
      managerLevel: null,
      managerBusinessMobile: null,
      directorId: null,
      directorLevel: null,
      whenUnavailableId: null,
      whenUnavailableLevel: null,
      jobRoleVersion: null,
      jobRoleItem: null,
      jobRole: null,
      raw: null,
      id: null,
      version: 0,
      availabilities: [],
      isUnavailable: 'FALSE',
      dirtyStatus: DirtyStatus.NEW,
    } as unknown as Contact;
  }

  buildUpdateStatement(contact: Contact, changes: any): ContactUpdateDto {
    const retVal: ContactUpdateDto = {
      id: contact.id,
      expectedVersion: contact.version,
    };

    Object.keys(changes).forEach((key) => {
      if (
        key !== 'supervisor' &&
        key !== 'afterHoursContactMethod' &&
        key !== 'pagerPin'
      ) {
        if (changes[key] || changes[key] === '') {
          retVal[key] = changes[key];
        }
      }
    });

    retVal.businessPhone =
      changes.businessPhone || changes.businessPhone === ''
        ? changes.businessPhone
        : contact.businessPhone;
    retVal.businessMobile =
      changes.businessMobile || changes.businessMobile === ''
        ? changes.businessMobile
        : contact.businessMobile;
    retVal.home =
      changes.home || changes.home === '' ? changes.home : contact.home;
    retVal.other =
      changes.other || changes.other === '' ? changes.other : contact.other;
    retVal.note =
      changes.note || changes.note === '' ? changes.note : contact.note;
    retVal.personalMobile =
      changes.personalMobile || changes.personalMobile === ''
        ? changes.personalMobile
        : contact.personalMobile;
    retVal.speedDial =
      changes.speedDial || changes.speedDial === ''
        ? changes.speedDial
        : contact.speedDial;
    retVal.whenUnavailable = changes.whenUnavailable
      ? changes.whenUnavailable.id
      : null;

    if (changes.supervisor) {
      retVal.reportsTo = changes.supervisor.id ?? changes.supervisor;
    }

    if (
      changes.afterHoursContactMethod ||
      changes.afterHoursContactMethod === ''
    ) {
      retVal.afterHoursPhoneNumber = changes.afterHoursContactMethod;
    }

    if (changes.city) {
      retVal.city = changes.city;
    }

    const avails: any[] = [];
    contact.availabilities.forEach((a) => {
      if (a.dirtyStatus === DirtyStatus.UPDATED) {
        // Need to correct the dates here otherwise we will infinitely go 4 hours into the future
        avails.push({
          id: a.id,
          ContactId: a.ContactId,
          start: a.start,
          end: a.end,
          dirtyStatus: a.dirtyStatus,
          expectedVersion: a.version,
        });
      } else if (a.dirtyStatus === DirtyStatus.NEW) {
        avails.push({
          id: '',
          ContactId: a.ContactId,
          start: a.start,
          end: a.end,
          dirtyStatus: a.dirtyStatus,
          expectedVersion: 0,
        });
      } else if (a.dirtyStatus === DirtyStatus.DELETED) {
        avails.push({
          id: a.id,
          ContactId: a.ContactId,
          dirtyStatus: a.dirtyStatus,
          expectedVersion: a.version,
        });
      }
    });

    if (avails.length > 0) {
      retVal.availabilities = avails;
    }

    return retVal;
  }

  buildCreateObject(contact: any): ContactUpdateDto {
    const retVal: ContactUpdateDto = {
      id: '',
      firstName: contact.firstName,
      lastName: contact.lastName,
      nickName: contact.nickName,
      company: contact.company,
      reportsTo: contact.supervisor ? contact.supervisor.id : null,
      adAccountName: contact.email ? contact.email : null,
      afterHoursPhoneNumber: contact.afterHoursContactMethod,
      speedDial: contact.speedDial,
      jobRole: contact.jobRole,
      businessPhone: contact.businessPhone ? contact.businessPhone : null,
      businessMobile: contact.businessMobile ? contact.businessMobile : null,
      home: contact.home ? contact.home : null,
      other: contact.other ? contact.other : null,
      note: contact.note ? contact.note : null,
      personalMobile: contact.personalMobile ? contact.personalMobile : null,
      inactiveAdAccount: false,
      whenUnavailable: contact.whenUnavailable
        ? contact.whenUnavailable.id
        : null,
      expectedVersion: 0,
      city: contact.city,
    };

    return retVal;
  }

  buildForm(fb: UntypedFormBuilder, contact: any): UntypedFormGroup {
    const phoneValidator = Validators.pattern(
      /^$|^\d{3}-\d{3}-\d{4}$|^\+\d{1,3}-\d{2}-\d{4}-\d{4}$|^\+\d{1,3}-\d{3}-\d{3}-\d{4}$/
    );
    const retVal: UntypedFormGroup = fb.group({
      firstName: [contact.firstName, Validators.required],
      lastName: [contact.lastName, Validators.required],
      nickName: [contact.nickName],
      company: [
        contact.companyItem ? contact.companyItem.value : null,
        Validators.required,
      ],
      supervisor: [
        contact.supervisor
          ? { name: contact.supervisor, id: contact.reportsToId }
          : null,
        Validators.required,
      ],
      managerLevel: [{ value: contact.managerLevel, disabled: true }],
      directorLevel: [{ value: contact.directorLevel, disabled: true }],
      whenUnavailable: contact.whenUnavailableLevel
        ? { name: contact.whenUnavailableLevel, id: contact.whenUnavailableId }
        : null,
      activeStatus: [{ value: contact.activeStatus, disabled: true }],
      email: [
        {
          value: contact.adAccountName,
          disabled:
            !isNullOrUndefined(contact) && !isNullOrUndefined(contact.id),
        },
        Validators.required,
      ],
      businessPhone: [
        contact.businessPhone,
        [Validators.required, phoneValidator],
      ],
      businessMobile: [contact.businessMobile, [phoneValidator]],
      jobRole: [
        contact.jobRoleItem ? contact.jobRoleItem.value : null,
        Validators.required,
      ],
      home: [contact.home, [phoneValidator]],
      other: [contact.other, [phoneValidator]],
      note: [contact.note],
      personalMobile: [contact.personalMobile, [phoneValidator]],
      speedDial: [contact.speedDial],
      afterHoursContactMethod: [
        contact.afterHoursContactMethod
          ? contact.afterHoursContactMethod.value
          : null,
      ],
      city: [contact.city, Validators.required],
      id: [contact.id],
      version: [contact.version],
    });

    return retVal;
  }

  updateForm(form: UntypedFormGroup, contact: any, writeAccess: boolean) {
    form.patchValue({
      firstName: contact.firstName,
      lastName: contact.lastName,
      nickName: contact.nickName,
      company: contact.companyItem ? contact.companyItem.value : null,
      supervisor: {
        name: contact.supervisor,
        id: contact.reportsToId,
      },
      managerLevel: contact.managerLevel,
      directorLevel: contact.directorLevel,
      whenUnavailable: contact.whenUnavailableId
        ? {
            name: contact.whenUnavailableLevel,
            id: contact.whenUnavailableId,
          }
        : null,
      activeStatus: contact.activeStatus,
      email: contact.adAccountName,
      businessPhone: contact.businessPhone,
      businessMobile: contact.businessMobile,
      jobRole: contact.jobRoleItem ? contact.jobRoleItem.value : null,
      home: contact.home,
      other: contact.other,
      note: contact.note,
      personalMobile: contact.personalMobile,
      speedDial: contact.speedDial,
      afterHoursContactMethod: contact.afterHoursContactMethod
        ? contact.afterHoursContactMethod.value
        : null,
      city: contact.city,
      id: contact.id,
      version: contact.version,
    });
    form.markAsPristine();
    form.markAsUntouched();
    form.controls['email'].disable();

    if (writeAccess === false) {
      form.disable();
    }
  }

  buildContactConnection(item: any): ContactConnection {
    return {
      items: item.items.map((contact: any) => this.buildContact(contact)),
      totalRecords: item.totalRecords,
    };
  }

  buildContactDropdown(item: any): ContactDropdown {
    return {
      id: item.id,
      firstName: item.firstName,
      lastName: item.lastName,
    };
  }

  buildContactDropdownConnection(item: any): ContactDropdownConnection {
    return {
      items: item.items.map((contact: any) =>
        this.buildContactDropdown(contact)
      ),
      totalRecords: item.totalRecords,
    };
  }

  buildContactExistResult(item: any): ContactExistResult {
    return {
      existing: item.existing,
      success: item.success,
      contactId: item.contactId,
      consentGivenAt: getCorrectedDate(item.consentGivenAt)!,
    };
  }

  buildJobRole(item: any): JobRole {
    return {
      id: item.id,
      name: item.name,
      enabled: item.enabled,
      version: item.version,
      shiftGroups: item?.shiftGroups?.map((i: any) =>
        this._mocBuilder.buildShiftGroup(i)
      ),
      shiftGroupsStr: item?.shiftGroups?.map((i: any) => i.name).join(', '),
    };
  }

  buildJobRoleConnection(item: any): JobRoleConnection {
    return {
      items: item.items.map((i: any) => this.buildJobRole(i)),
      totalRecords: item.totalRecords,
    };
  }
}
